import { useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { createAskSession } from '@/apis/ask/askHistory/post/createAskSession';
import { invokeAsk } from '@/apis/ask/ask/post/invokeAsk';
import { AskType } from '@/apis/ask/ask/types';
import { AuthContextProps } from '@/contexts/AuthContext';
import { formatDateTime } from '@/components/pages/ask/AskSession.utils';
import { DEFAULT_CHAT_PERSONA_ID } from '@/components/pages/landingPage/Landingpage.constants';
import { compileVisualPrompt } from '@/utils/visualPrompts/compileVisualPrompt';
import type {
  VisualPromptAnswers,
  VisualPromptDefinition,
} from '@/types/visualPrompts/visualPrompt.types';
import { useWorkspaceErrorHandler } from '@/hooks/useWorkspaceErrorHandler';
import { VISUAL_PROMPT_MESSAGES } from '@/constants/visualPrompts/messages';
import { normalizeVisualPromptError } from '../useVisualPrompts/utils';

interface UseRunVisualPromptParams {
  workspaceId: string;
  getAccessToken: AuthContextProps['getAccessToken'];
}

export const useRunVisualPrompt = ({
  workspaceId,
  getAccessToken,
}: UseRunVisualPromptParams) => {
  const navigate = useNavigate();
  const { handleError, handleSuccess } = useWorkspaceErrorHandler(workspaceId);
  const [isRunning, setIsRunning] = useState(false);

  const runVisualPrompt = useCallback(
    async (
      definition: VisualPromptDefinition,
      answers: VisualPromptAnswers,
    ): Promise<boolean> => {
      const inputText = compileVisualPrompt(definition, answers).trim();
      if (!inputText) {
        handleError(
          new Error('Compiled prompt is empty'),
          VISUAL_PROMPT_MESSAGES.runError,
        );
        return false;
      }

      setIsRunning(true);
      try {
        const title = `${formatDateTime(new Date())} ${definition.title}`;
        const response = await createAskSession({
          getAccessToken,
          data: {
            workspaceId,
            title,
            askPersonaId: definition.personaId ?? DEFAULT_CHAT_PERSONA_ID,
          },
        });

        const sessionId = response.askSession?.id;
        if (!sessionId) {
          throw new Error('Create Ask Session did not return an askSession id');
        }

        await invokeAsk({
          getAccessToken,
          askSessionId: sessionId,
          data: {
            input: inputText,
            inputTypeIndex: 0,
            variables: [],
            selectedDocuments: [],
            documentCollections: [],
            workspaceId,
            askPromptId: response.askSession?.initialAskPromptId ?? '',
            askType: AskType.MESSAGE,
          },
        });

        handleSuccess(VISUAL_PROMPT_MESSAGES.runSuccess);
        navigate(`/workspace/${workspaceId}/ask-session/${sessionId}`);
        return true;
      } catch (err) {
        const normalizedError = normalizeVisualPromptError(err);
        console.error('[useRunVisualPrompt] Run Failed:', normalizedError);
        handleError(normalizedError, VISUAL_PROMPT_MESSAGES.runError);
        return false;
      } finally {
        setIsRunning(false);
      }
    },
    [workspaceId, getAccessToken, navigate, handleError, handleSuccess],
  );

  return { runVisualPrompt, isRunning };
};
